import FoldersCollection    from "./FoldersCollection";
import FolderItem           from "../Entities/FolderItem";
import LocalStorageService  from "@/js/Shared/Services/Storage/LocalStorageService.js"
import CollectionList       from '@/js/Shared/Collections/CollectionList';
import SimpleCollectionList from '@/js/Shared/Collections/SimpleCollectionList';
import Language             from "@/js/Shared/Language/Language";
import SAPOMailConfig       from "@/SAPOMailConfig";

export { FoldersCollapseCollection as default };

/**
* Folders Slice Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class FoldersCollapseCollection extends CollectionList {
  static MODIFIERS_MAP = {
    'add_top_folder'  : 'modifyAddTopFolder',
    'add_all_folders' : 'modifyAddAllFolders',
  }

  /**
   * Contacts Collection Lists data
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {FoldersCollection}
   */
  folders;

  /**
   * Defines the list id for storage purposes
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  list_id = null;

  /**
   * Holds all collapsed folders
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  collapsed = {};

  /**
   * Collection modifiers
   *
   * @protected
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  modifiers = {};

  /**
   * Expand delay
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {Number}
   */
  expand_delay = 1000;

  /**
   * List
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {SimpleCollectionList}
   */
  expand_folders_timeout = new SimpleCollectionList();

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   */
  constructor(list_id) {
    super('id');
    this.list_id = list_id;
    this.loadState();
  }

  /**
   * Sets collection modifiers
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} name modifier name
   *
   * @return {void}
   */
  setModifier(name) {
    this.modifiers[name] = true;
  }

  /**
   * Folders collection
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FoldersCollection}
   *
   * @return {void}
   */
  setFolders(folders) {
    this.folders = folders;
    this.reload();
  }

  /**
   * Checks if folder is collapsed
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} id
   *
   * @return {Boolean}
   */
  isCollapsed(id) {
    return this.collapsed[id] || false;
  }

  /**
   * Collapses or expands a folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id
   *
   * @return {void}
   */
  toggleCollapse(folder_id) {
    this.collapsed[folder_id] ? this.expand(folder_id) : this.collapse(folder_id);
  }

  /**
   * Adds a folder to the collapsed stack
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id
   *
   * @return {void}
   */
  collapse(folder_id) {
    this.collapsed[folder_id] = true;
    this.reload();
  }

  /**
   * Removes a folder ftom the collapsed stack
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id
   *
   * @return {void}
   */
  expand(folder_id) {
    delete this.collapsed[folder_id];
    this.reload();
  }

  /**
   * Expands a folder after a delay
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id
   *
   * @return {void}
   */
  expandDelay(folder_id) {
    if (this.isCollapsed(folder_id) === false) {
      return;
    }

   let timeout = setTimeout(() => {
      this.expand(folder_id);
      this.expandDelayStop(folder_id);
    }, this.expand_delay)

    this.expand_folders_timeout.add(timeout, folder_id);
  }

  /**
   * Stops the expansion of folder that is set to be expanded with the delay
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id
   *
   * @return {void}
   */
  expandDelayStop(folder_id) {
    clearInterval(this.expand_folders_timeout.get(folder_id));
    this.expand_folders_timeout.delete(folder_id)
  }


  /**
   * Removes all folders ftom the collapsed stack
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  expandAll() {
    this.collapsed = {};
    this.reload();
  }

  /**
   * Add all folders to the collapsed stack
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  collapseAll() {
    this.collapsed = {};

    if(this.folders){
      this.folders.items.forEach(folder => {
        if (folder.childs) {
          this.collapsed[folder.id] = true;
        }
      });

      this.reload();
    }
  }

  /**
   * Reloads the collection list
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reload() {
    this.reset();

    if (!this.folders || this.folders.count <= 0) {
      return;
    }

    this.applyModifiers();
    this.slice();
  }

  /**
   * Applies Collection modifiers
   *
   * @public
   *
   * @since 7.0.0
   *   *
   * @return {void}
   */
  applyModifiers() {
    for (let modifier in this.modifiers) {
      this[FoldersCollapseCollection.MODIFIERS_MAP[modifier]]();
    }
  }

  /**
   * Modifies items list
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} group_ids group_ids
   *
   * @return {void}
   */
  modifyAddTopFolder() {
    this.add(this.getTopFolderItem());
  }

  /**
   * Modifies items list
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} group_ids group_ids
   *
   * @return {void}
   */
  modifyAddAllFolders() {
    this.add(this.getAllFoldersItem());
  }

  /**
   * Get a FolderItem for the folder
   * symbolizes TOPFOLDER
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {FolderItem}
   */
  getTopFolderItem(){
    const folder  = new FolderItem();
    folder.id   = "_TOPFOLDER_"
    folder.name = Language.say('folders.top_folder_name');
    folder.icon = SAPOMailConfig.client.folders.icons._TOPFOLDER_;
    return folder;
  }

  /**
   * Adds a FolderItem for the folder
   * symbolizes ALLFOLDERS
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {void}
   */
  getAllFoldersItem() {
    const folder  = new FolderItem();
    folder.id   = "YWxsAA"
    folder.name = Language.say('folders.all_folders_name');
    folder.icon = SAPOMailConfig.client.folders.icons._ALLFOLDERS_;
    return folder;
  }

  /**
   * Slices set items according to the collapse state
   * into the folders array
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  slice() {
    let ignore = [];

    this.folders.items.forEach(folder => {
      if (ignore[folder.parent]) {
        ignore[folder.id] = true;
        return;
      }

      if (!folder.childs && this.collapsed[folder.id]) {
        delete this.collapsed[folder.id];
        return;
      }

      if (this.collapsed[folder.id]) {
        ignore[folder.id] = true;
      }

      this.add(folder);
    });

    this.saveState();
  }

  /**
   * Saves Slice controller state in local storage
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  saveState() {
    LocalStorageService.setObject('fsc_' + this.list_id, this.collapsed);
  }

  /**
   * Loads controller state from local storage service
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  loadState() {
    this.collapsed = LocalStorageService.getObject('fsc_' + this.list_id, {});
  }
}